import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CustomModalService } from '@piqet-angular/shared';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { Subscription } from 'rxjs';

interface OrganizationData {
  id?: number;
  name: string;
  materialName: {
    id: number;
    description: string;
    materialId: number;
  } | null;
  conversionName: {
    id: number;
    name: string;
    conversionId: number;
  } | null;
}

@Component({
  selector: 'piqet-angular-new-organization-data-map',
  templateUrl: './new-organization-data-map.component.html',
  styleUrls: ['./new-organization-data-map.component.scss'],
})
export class NewOrganizationDataMapComponent implements OnInit {
  modalLabel = 'Add organization map data';
  OrganizationDataForm!: FormGroup;
  submitted = false;
  duplicateError = '';

  @Output() saveOrganizationDataEvent = new EventEmitter<any>();

  organizationDataRecords: OrganizationData[] = [];
  editingRecordId: number | null = null;
  materialList: any;
  conversionList: any;
  existingData: any;
  organizationData: any;

  constructor(
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private modal: CustomModalService
  ) {
    this.materialList = this.modal?.modalService?.config?.initialState?.['materialList'];
    this.conversionList = this.modal?.modalService?.config?.initialState?.['conversionList'];
    this.existingData = this.modal?.modalService?.config?.initialState?.['existingData'];
    this.organizationData = this.modal?.modalService?.config?.initialState?.['organizationData'];
  }

  ngOnInit(): void {
    this.initForm();
  }

  // Custom validator to check if either material or conversion is selected
  atLeastOneRequired(control: AbstractControl): ValidationErrors | null {
    const materialName = control.get('materialName');
    const conversionName = control.get('conversionName');

    if (!materialName || !conversionName) {
      return { atLeastOneRequired: true };
    }

    if (!materialName.value && !conversionName.value) {
      return { atLeastOneRequired: true };
    }

    return null;
  }

  initForm(): void {
    this.OrganizationDataForm = this.formBuilder.group(
      {
        name: ['', [Validators.required]],
        materialName: [null],
        conversionName: [null],
      },
      { validators: this.atLeastOneRequired }
    );
  }

  checkNameExists(): void {
    const enteredName = this.OrganizationDataForm.get('name')?.value;
    let duplicateInOrg = false;
    let duplicateInExisting = false;
    if (enteredName) {
      duplicateInExisting = this.existingData.some(
        (data: any) => data.name?.trim().toLowerCase() == enteredName?.trim().toLowerCase()
      );
      if (this.organizationData?.length) {
        duplicateInOrg = this.organizationData?.some(
          (data: any) => data.name?.trim().toLowerCase() == enteredName?.trim().toLowerCase()
        );
      }
      this.duplicateError =
        duplicateInExisting || duplicateInOrg ? 'A mapping with this name already exists.' : '';
    }
  }

  isDuplicate(newRecord: OrganizationData): boolean {
    return this.organizationDataRecords.some(record => {
      const nameMatch = record.name?.trim().toLowerCase() == newRecord.name?.trim().toLowerCase();
      return nameMatch;
    });
  }

  onAddRecord(): void {
    this.submitted = true;

    if (this.OrganizationDataForm.valid && !this.duplicateError) {
      const formValue = this.OrganizationDataForm.value;
      const newRecord: OrganizationData = {
        id: this.organizationDataRecords.length + 1,
        ...formValue,
      };

      if (this.isDuplicate(newRecord)) {
        this.duplicateError = 'A mapping with this name already exists.';
        return;
      }

      this.organizationDataRecords.push(newRecord);
      this.resetForm();
    }
  }

  commonInputSearchDes(term: string, item: any): boolean {
    return item.description.toLowerCase().startsWith(term.toLowerCase());
  }

  commonInputSearch(term: string, item: any): boolean {
    return item.name.toLowerCase().startsWith(term.toLowerCase());
  }

  onClose(): void {
    this.modal.close();
    this.resetForm();
  }

  resetForm(): void {
    this.OrganizationDataForm.reset();
    this.editingRecordId = null;
    this.submitted = false;
    this.duplicateError = '';
  }

  onDelete(record: OrganizationData): void {
    this.organizationDataRecords = this.organizationDataRecords.filter(r => r.id !== record.id);
  }

  onSave() {
    if (this.organizationDataRecords.length) {
      this.saveOrganizationDataEvent.emit(this.organizationDataRecords);
    }
  }

  get hasValidationError(): boolean {
    return (
      this.submitted &&
      this.OrganizationDataForm.errors?.['atLeastOneRequired'] &&
      !this.OrganizationDataForm.get('materialName')?.value &&
      !this.OrganizationDataForm.get('conversionName')?.value
    );
  }
}
